import { useState, useRef, useEffect } from "react";
import {
  Box,
  Fab,
  Zoom,
  TextField,
  Typography,
  IconButton,
  Avatar,
  Paper,
  InputAdornment,
  Badge,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { styled } from "@mui/material/styles";
import { useSessionContext } from "../../contexts/SessionContext";
import { useFormatter } from "../../utils/useFormatter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import luxor from "../../assets/luxor.png";
import luxorInvesting from "../../assets/investigar_saude_icon2.png";
import {
  sendMessageToWebhook,
  sendMessageToWebhookPractitioner,
} from "@/services/n8n/chatLuxor";
import { useAxios } from "@/utils/useAxios";
import { useAuth } from "@/utils/useAuth";
import { oids } from "@/configs/Settings";

const LuxorContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 25,
  right: 40,
  zIndex: 9999,
  [theme.breakpoints.down("sm")]: {
    bottom: 60,
  },
}));

const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
  position: "fixed",
  bottom: 80,
  right: 20,
  zIndex: 9998,
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
  width: isExpanded ? 550 : 400,
  height: isExpanded ? 700 : 500,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fff",
  transition: "all 0.3s ease-in-out",

  [theme.breakpoints.down("sm")]: {
    width: isExpanded ? "95%" : "90%",
    height: isExpanded ? "80%" : "60%",
    right: isExpanded ? "2.5%" : "5%",
    bottom: 110,
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  backgroundColor: "#00ae9a",
  color: "white",
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
}));

const ChatBody = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: "auto",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  backgroundColor: "#f8f9fa",
  maxWidth: "100%",
  overflowX: "hidden",
}));

const ChatInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, 0.06)",
  backgroundColor: "#fff",
}));

const MessageLuxor = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: isUser ? "flex-end" : "flex-start",
  marginBottom: theme.spacing(1),
  width: "100%",
}));

const MessageContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  maxWidth: "100%",
  width: "fit-content",
  padding: theme.spacing(1.5, 2),
  borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
  backgroundColor: isUser ? "#00ae9a" : "white",
  color: isUser ? "white" : "#3a3a3a",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
  position: "relative",
  wordBreak: "break-word",
  overflow: "hidden",
  "& a": {
    color: isUser ? "#e0f7fa" : "#0277bd",
    textDecoration: "none",
    fontWeight: 500,
    wordBreak: "break-all",
    overflowWrap: "break-word",
    display: "inline-block",
    maxWidth: "100%",
    position: "relative",
    padding: "0 2px",
    borderRadius: "3px",
    backgroundColor: isUser
      ? "rgba(255, 255, 255, 0.15)"
      : "rgba(2, 119, 189, 0.08)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isUser
        ? "rgba(255, 255, 255, 0.25)"
        : "rgba(2, 119, 189, 0.15)",
      color: isUser ? "#ffffff" : "#0277bd",
      textDecoration: "underline",
    },
    "&::after": {
      content: '""',
      display: "inline-block",
      width: "12px",
      height: "12px",
      marginLeft: "4px",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='14' height='14' fill='${
        isUser ? "%23e0f7fa" : "%230277bd"
      }'%3E%3Cpath d='M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "contain",
      verticalAlign: "middle",
    },
  },
  "& code": {
    fontFamily: "monospace",
    backgroundColor: isUser
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(0, 0, 0, 0.05)",
    padding: "2px 4px",
    borderRadius: 4,
    fontSize: "0.9em",
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
  },
  "& pre": {
    margin: "8px 0",
    borderRadius: 8,
    overflow: "auto",
    maxWidth: "100%",
  },
  "& ul, & ol": {
    paddingLeft: 20,
    marginTop: 8,
    marginBottom: 8,
  },
  "& blockquote": {
    borderLeft: `4px solid ${
      isUser ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.1)"
    }`,
    paddingLeft: 8,
    margin: "8px 0",
    fontStyle: "italic",
  },
  "& table": {
    borderCollapse: "collapse",
    width: "100%",
    margin: "8px 0",
    tableLayout: "fixed",
  },
  "& th, & td": {
    border: `1px solid ${
      isUser ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"
    }`,
    padding: "4px 8px",
    textAlign: "left",
    wordBreak: "break-word",
  },
  "& th": {
    backgroundColor: isUser
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
  },
  "& img": {
    maxWidth: "100%",
    borderRadius: 4,
    margin: "8px 0",
  },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    margin: "8px 0 4px 0",
  },
}));

const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: "0.675rem",
  color: "rgba(0, 0, 0, 0.5)",
  marginTop: 4,
  textAlign: "right",
}));

const AvatarContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 8px",
}));

const TypingDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  backgroundColor: "#00ae9a",
  borderRadius: "50%",
  opacity: 0.7,
  animation: "typingAnimation 1.4s infinite ease-in-out",
  "&:nth-of-type(1)": {
    animationDelay: "0s",
  },
  "&:nth-of-type(2)": {
    animationDelay: "0.2s",
  },
  "&:nth-of-type(3)": {
    animationDelay: "0.4s",
  },
  "@keyframes typingAnimation": {
    "0%": {
      transform: "translateY(0px)",
      opacity: 0.7,
    },
    "50%": {
      transform: "translateY(-5px)",
      opacity: 1,
    },
    "100%": {
      transform: "translateY(0px)",
      opacity: 0.7,
    },
  },
}));

const AvatarSelector = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: 60,
  backgroundColor: "#f0f0f0",
  borderRight: "1px solid rgba(0, 0, 0, 0.1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(2, 0),
  overflowY: "auto",
  zIndex: 1,
}));

const AssistantAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  width: 48,
  height: 48,
  cursor: "pointer",
  margin: theme.spacing(1, 0),
  border: isSelected ? "2px solid #00ae9a" : "2px solid transparent",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

type BodyBuilderFunction = (
  param1: string,
  param2?: string | null | undefined
) => Promise<any> | any;

type Assistant = {
  id: string;
  name: string;
  avatar: string;
  endpoint: string;
  welcomeMessage: string;
  usePractitionerSuffix?: boolean;
  bodyBuilder?: BodyBuilderFunction;
};

const defaultBodyBuilder: BodyBuilderFunction = (message, accessType) => ({
  message,
  user: accessType || "",
});

const assistants: Assistant[] = [
  {
    id: "luxor",
    name: "Assistente Virtual",
    avatar: luxor,
    endpoint: "chat-support-luxor",
    welcomeMessage: "Olá! Como posso ajudar você hoje?",
    usePractitionerSuffix: true,
    bodyBuilder: defaultBodyBuilder,
  },
];

type Message = {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
};

const CodeBlock = styled("pre")(({ theme }) => ({
  backgroundColor: "#2b2b2b",
  color: "#fff",
  padding: theme.spacing(2),
  borderRadius: 8,
  overflow: "auto",
  fontSize: "0.85em",
  margin: "8px 0",
  maxWidth: "100%",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  "& code": {
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
}));

export interface ChatLuxorEvents {
  openChat: (assistantId: string, initialMessage?: string) => void;
  clearChat: (assistantId: string) => void;
}

class ChatLuxorManager {
  private static instance: ChatLuxorManager;
  private eventHandlers: ChatLuxorEvents | null = null;

  private constructor() {}

  public static getInstance(): ChatLuxorManager {
    if (!ChatLuxorManager.instance) {
      ChatLuxorManager.instance = new ChatLuxorManager();
    }
    return ChatLuxorManager.instance;
  }

  public registerHandlers(handlers: ChatLuxorEvents): void {
    this.eventHandlers = handlers;
  }

  public openChat(assistantId: string, initialMessage?: string): void {
    if (this.eventHandlers) {
      this.eventHandlers.openChat(assistantId, initialMessage);
    }
  }

  public clearChat(assistantId: string): void {
    if (this.eventHandlers) {
      this.eventHandlers.clearChat(assistantId);
    }
  }
}

export const chatLuxorManager = ChatLuxorManager.getInstance();

const STORAGE_KEY_PREFIX = "chatLuxor_messages_";

const saveMessagesToStorage = (assistantId: string, messages: Message[]) => {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${assistantId}`,
      JSON.stringify(messages)
    );
  } catch (error) {
    console.error("Erro ao salvar mensagens no localStorage:", error);
  }
};

const loadMessagesFromStorage = (assistantId: string): Message[] => {
  try {
    const storedMessages = localStorage.getItem(
      `${STORAGE_KEY_PREFIX}${assistantId}`
    );
    if (storedMessages) {
      const parsedMessages = JSON.parse(storedMessages);
      return parsedMessages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (error) {
    console.error("Erro ao carregar mensagens do localStorage:", error);
  }
  return [];
};

const clearMessagesFromStorage = (assistantId: string) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${assistantId}`);
  } catch (error) {
    console.error("Erro ao limpar mensagens do localStorage:", error);
  }
};

export function ChatLuxor() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant>(
    assistants[0]
  );
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const { user, access } = useSessionContext();

  useEffect(() => {
    if (selectedAssistant) {
      const storedMessages = loadMessagesFromStorage(selectedAssistant.id);
      if (storedMessages && storedMessages.length > 0) {
        setMessages(storedMessages);
      } else {
        setMessages([
          {
            id: "1",
            text: selectedAssistant.welcomeMessage,
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    }
  }, [selectedAssistant.id]);

  useEffect(() => {
    if (messages.length > 0 && selectedAssistant) {
      saveMessagesToStorage(selectedAssistant.id, messages);
    }
  }, [messages, selectedAssistant.id]);

  useEffect(() => {
    chatLuxorManager.registerHandlers({
      openChat: (assistantId: string, initialMessage?: string) => {
        const assistant = assistants.find((a) => a.id === assistantId);
        if (assistant) {
          setSelectedAssistant(assistant);
          setIsOpen(true);

          if (initialMessage) {
            setTimeout(() => {
              const userMessage: Message = {
                id: Date.now().toString(),
                text: initialMessage,
                isUser: true,
                timestamp: new Date(),
              };

              const existingMessages = loadMessagesFromStorage(assistant.id);
              if (existingMessages && existingMessages.length > 0) {
                setMessages([...existingMessages, userMessage]);
              } else {
                setMessages([
                  {
                    id: "1",
                    text: assistant.welcomeMessage,
                    isUser: false,
                    timestamp: new Date(),
                  },
                  userMessage,
                ]);
              }

              sendMessageToLuxor(initialMessage);
            }, 500);
          }
        }
      },
      clearChat: (assistantId: string) => {
        if (assistantId === selectedAssistant.id) {
          setMessages([
            {
              id: "1",
              text: selectedAssistant.welcomeMessage,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
          clearMessagesFromStorage(assistantId);
        }
      },
    });
  }, [selectedAssistant]);

  const handleClearChat = () => {
    setMessages([
      {
        id: "1",
        text: selectedAssistant.welcomeMessage,
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    clearMessagesFromStorage(selectedAssistant.id);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSelectAssistant = (assistant: Assistant) => {
    setSelectedAssistant(assistant);
  };

  const sendMessageToLuxor = async (message: string) => {
    try {
      setIsLoading(true);

      let endpoint = `${import.meta.env.VITE_ENDPOINT_N8N}/${
        selectedAssistant.endpoint
      }`;

      if (
        selectedAssistant.usePractitionerSuffix &&
        access.type !== "patient"
      ) {
        endpoint += "-practitioner";
      }

      let requestBody;
      const bodyBuilder = selectedAssistant.bodyBuilder || defaultBodyBuilder;
      requestBody = await Promise.resolve(
        bodyBuilder(message, access.type || "")
      );

      const response = await useAxios.post(endpoint, requestBody, {
        headers: {
          Authorization: await useAuth.getUserToken(),
        },
      });

      let responseText = "";
      if (response && response.data) {
        if (response.data.output) {
          responseText = response.data.output;
        } else if (response.data.response) {
          responseText = response.data.response;
        } else if (response.data.text) {
          responseText = response.data.text;
        } else if (typeof response.data === "string") {
          responseText = response.data;
        } else {
          responseText = JSON.stringify(response.data);
        }

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          isUser: false,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "Desculpe, tive um problema ao processar sua mensagem. Por favor, tente novamente.",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() === "") return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: newMessage,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageToSend = newMessage;
    setNewMessage("");

    await sendMessageToLuxor(messageToSend);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const userName = user?.name
    ? useFormatter.formatNameDisplay(user.name.split(" ")[0])
    : "Visitante";

  return (
    <>
      <Tooltip title="Chat com Luxor">
        <LuxorContainer>
          <Badge
            color="error"
            badgeContent={unreadCount}
            invisible={unreadCount === 0 || isOpen}
          >
            <Fab
              color="primary"
              aria-label="chat"
              onClick={toggleChat}
              sx={{
                backgroundColor: "#00ae9a",
                "&:hover": {
                  backgroundColor: "#009688",
                  transform: "scale(1.05)",
                },
                width: 56,
                height: 56,
                transition: "transform 0.2s ease-in-out",
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              {isOpen ? <CloseIcon /> : <ChatIcon />}
            </Fab>
          </Badge>
        </LuxorContainer>
      </Tooltip>

      <Zoom in={isOpen} unmountOnExit>
        <ChatContainer elevation={4} isExpanded={isExpanded}>
          <AvatarSelector>
            {assistants.map((assistant) => (
              <AssistantAvatar
                key={assistant.id}
                src={assistant.avatar}
                alt={assistant.name}
                isSelected={selectedAssistant.id === assistant.id}
                onClick={() => handleSelectAssistant(assistant)}
                title={assistant.name}
              />
            ))}
          </AvatarSelector>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              paddingLeft: "60px",
            }}
          >
            <ChatHeader>
              <AvatarContainer>
                <Avatar
                  src={selectedAssistant.avatar}
                  alt={selectedAssistant.name}
                  sx={{ width: 36, height: 36 }}
                />
                <Box>
                  <Typography variant="body1" fontWeight={400}>
                    {selectedAssistant.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {isLoading ? "Digitando..." : "Online"}
                  </Typography>
                </Box>
              </AvatarContainer>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Limpar conversa">
                  <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleClearChat}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={toggleExpand}
                  size="small"
                >
                  {isExpanded ? (
                    <CloseFullscreenIcon fontSize="small" />
                  ) : (
                    <OpenInFullIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={toggleChat}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </ChatHeader>
            <ChatBody sx={{ flex: 1, overflow: "auto" }}>
              {messages.map((message) => (
                <MessageLuxor key={message.id} isUser={message.isUser}>
                  {!message.isUser && (
                    <Avatar
                      src={selectedAssistant.avatar}
                      alt={selectedAssistant.name}
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 1,
                        display: message.isUser ? "none" : "block",
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      maxWidth: message.isUser ? "90%" : "calc(100% - 40px)",
                      width: "fit-content",
                    }}
                  >
                    <MessageContent isUser={message.isUser}>
                      {message.isUser ? (
                        <span
                          style={{
                            display: "inline-block",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                            fontFamily: "inherit",
                            fontSize: "0.875rem",
                            lineHeight: "1.43",
                            letterSpacing: "0.01071em",
                          }}
                        >
                          {message.text}
                        </span>
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            code: ({ className, children }) => {
                              const isInlineCode = !className;
                              return isInlineCode ? (
                                <code
                                  style={{
                                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                                    padding: "2px 4px",
                                    borderRadius: 4,
                                    fontSize: "0.9em",
                                    wordBreak: "break-all",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {children}
                                </code>
                              ) : (
                                <CodeBlock>
                                  <code className={className}>{children}</code>
                                </CodeBlock>
                              );
                            },
                            a: ({ node, ...props }) => (
                              <a
                                {...props}
                                style={{
                                  wordBreak: "break-all",
                                  overflowWrap: "break-word",
                                  maxWidth: "100%",
                                  display: "inline-block",
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="chat-link"
                              />
                            ),
                          }}
                        >
                          {message.text}
                        </ReactMarkdown>
                      )}
                    </MessageContent>
                    <MessageTime>
                      {formatMessageTime(message.timestamp)}
                    </MessageTime>
                  </Box>
                </MessageLuxor>
              ))}
              {isLoading && (
                <MessageLuxor isUser={false}>
                  <Avatar
                    src={selectedAssistant.avatar}
                    alt={selectedAssistant.name}
                    sx={{
                      width: 32,
                      height: 32,
                      mr: 1,
                    }}
                  />
                  <MessageContent isUser={false}>
                    <TypingIndicator>
                      <TypingDot />
                      <TypingDot />
                      <TypingDot />
                    </TypingIndicator>
                  </MessageContent>
                </MessageLuxor>
              )}
              <div ref={messagesEndRef} />
            </ChatBody>

            <ChatInputContainer>
              <TextField
                fullWidth
                placeholder="Digite sua mensagem..."
                variant="outlined"
                size="small"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                multiline
                maxRows={3}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={newMessage.trim() === "" || isLoading}
                        sx={{
                          color: "#00ae9a",
                          transition: "transform 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress
                            size={24}
                            thickness={4}
                            sx={{ color: "#00ae9a" }}
                          />
                        ) : (
                          <SendIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    "&.Mui-focused fieldset": {
                      borderColor: "#00ae9a",
                    },
                  },
                }}
              />
            </ChatInputContainer>
          </Box>
        </ChatContainer>
      </Zoom>
    </>
  );
}
